import React, { useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { chatRoomQueries } from '../../../api/chat'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import { InfiniteScroll } from '../../common/InfiniteScroll'
import { Modal } from '../../common/Modal'
import { ModalBody } from '../../common/Modal/ModalBody'
import { ModalHeader } from '../../common/Modal/ModalHeader'
import { UserListItem } from '../../common/UserListItem/UserListItem'
import { ChipAvatar, chipFullName } from './ChipAvatar'
import { readerSubtitle } from './readerSubtitle'

interface ReadByModalProps {
  show: boolean
  close: () => void
  roomId: string
  messageId: string
}

export const ReadByModal = ({
  show,
  close,
  roomId,
  messageId,
}: ReadByModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const modalRef = useRef<HTMLDivElement | null>(null)

  const readersQuery = useInfiniteQuery({
    ...chatRoomQueries.readers(roomId, messageId),
    enabled: show,
  })
  const readers = readersQuery.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <Modal ref={modalRef} show={show}>
      {{
        header: (
          <ModalHeader onClose={close}>
            <p style={theme.textVariants.h3}>
              {translate('chatMessageReadBy.modalTitle')}
            </p>
          </ModalHeader>
        ),
        body: (
          <ModalBody className="max-h-96">
            <InfiniteScroll
              onEndReached={() =>
                readersQuery.fetchNextPage({ cancelRefetch: false })
              }
              isFetching={
                readersQuery.isLoading || readersQuery.isFetchingNextPage
              }
              rootRef={modalRef}
              className="flex flex-col gap-4"
            >
              {readers.map((row) => (
                <div key={row.userId} data-testid={`room-reader-${row.userId}`}>
                  <UserListItem
                    avatar={<ChipAvatar chip={row} />}
                    name={chipFullName(row)}
                    text={readerSubtitle(row)}
                    textStyle={theme.textVariants.baseBold}
                  />
                </div>
              ))}
            </InfiniteScroll>
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
